/**
 * Workspace usage (CLAUDE.md 12.1, 16.4).
 *
 * Visible to every member, not only to administrators, because a quota that refuses
 * an upload has to be explicable to the person whose upload was refused. "Storage
 * full" with no way to see what is using it is an error message that ends in a
 * support ticket.
 *
 * The breakdown is by *kind* rather than by project for the same reason: the useful
 * next action is "delete old reports" or "regenerate fewer drawings" (reports are
 * regenerable, not archival -- 8.1), and that is a decision about kinds.
 */
import { useTranslation } from 'react-i18next'

import { useWorkspaceUsage } from '@/api/workspaces'
import { useAuth } from '@/auth/AuthProvider'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'
import { formatBytes, formatNumber } from '@/lib/format'

export function UsagePanel() {
  const { t } = useTranslation()
  const { workspaceId } = useAuth()
  const { data: usage, isLoading } = useWorkspaceUsage(workspaceId)

  if (isLoading || !usage) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('usage.title')}</CardTitle>
        </CardHeader>
        <CardBody>
          <Skeleton className="h-32 w-full" />
        </CardBody>
      </Card>
    )
  }

  const quota = usage.quota_bytes > 0 ? usage.quota_bytes : 0
  const fraction = quota > 0 ? usage.total_bytes / quota : 0
  const over = fraction > 1
  const percent = formatNumber(Math.min(fraction, 999) * 100, { decimals: 0 })
  const remaining = Math.max(0, quota - usage.total_bytes)

  const breakdown: { key: string; bytes: number }[] = [
    { key: 'usage.reports', bytes: usage.reports_bytes },
    { key: 'usage.drawings', bytes: usage.drawings_bytes },
    { key: 'usage.attachments', bytes: usage.attachments_bytes },
    { key: 'usage.sheet_assets', bytes: usage.sheet_assets_bytes },
    { key: 'usage.logo', bytes: usage.logo_bytes },
  ]

  const counts: { key: string; value: number }[] = [
    { key: 'usage.projects', value: usage.projects },
    { key: 'usage.positions', value: usage.positions },
    { key: 'usage.calcs_7d', value: usage.calcs_7d },
    { key: 'usage.calcs_30d', value: usage.calcs_30d },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('usage.title')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-5">
        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-medium">{t('usage.storage')}</h3>
            <p className="tabular text-sm text-text-muted">
              {t('usage.of_quota', {
                used: formatBytes(usage.total_bytes),
                quota: formatBytes(quota),
              })}
            </p>
          </div>

          <div
            className="relative h-2.5 overflow-hidden rounded-full bg-[var(--util-bar-track)]"
            role="meter"
            aria-valuenow={Number(fraction.toFixed(3))}
            aria-valuemin={0}
            // Never clamped below the truth: telling a screen-reader user they are at
            // the limit when they are past it is worse than saying nothing (14.2).
            aria-valuemax={Math.max(1, Number(fraction.toFixed(3)))}
            aria-label={t('usage.storage')}
            aria-valuetext={t('usage.percent_used', { percent })}
          >
            <div
              className="h-full rounded-full transition-[width]"
              style={{
                width: `${Math.min(fraction, 1) * 100}%`,
                backgroundColor: over ? 'var(--util-bar-over)' : 'var(--util-bar)',
              }}
            />
          </div>

          {/* Words, not only a red bar. */}
          <p className={cn('text-xs', over ? 'text-[var(--status-fail)]' : 'text-text-muted')}>
            {over ? t('usage.over_quota') : t('usage.remaining', { value: formatBytes(remaining) })}
          </p>
        </section>

        <section className="space-y-1">
          <h3 className="text-sm font-medium">{t('usage.breakdown')}</h3>
          <ul className="divide-y divide-border text-sm">
            {breakdown.map(({ key, bytes }) => (
              <li key={key} className="flex items-center justify-between gap-3 py-1.5">
                <span className="text-text-muted">{t(key)}</span>
                <span className="tabular">{formatBytes(bytes)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-1">
          <h3 className="text-sm font-medium">{t('usage.activity')}</h3>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {counts.map(({ key, value }) => (
              <div key={key} className="rounded-md border border-border p-3">
                <dt className="text-xs text-text-muted">{t(key)}</dt>
                <dd className="tabular mt-1 text-lg font-semibold">
                  {formatNumber(value, { decimals: 0 })}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      </CardBody>
    </Card>
  )
}
